import React from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { PatientId } from '@/components/PatientId'
import { useAuth } from '@/hooks/useAuth'
import { UserProfileHelper } from '@lura/shared'

export const AdminPatientPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  // Study Admin access check
  if (!UserProfileHelper.isStudyAdmin(user)) {
    return <Navigate to="/studies" replace />
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  // Stub data
  const patient = {
    id: patientId,
    lastActivity: new Date('2025-11-17'),
    devices: [
      {
        id: '1',
        serialNumber: '00000001',
        study: {
          id: 'study-1',
          title: 'Fake Colgate Study',
          color: '#E91E63'
        },
        clinician: {
          firstName: 'Josh',
          lastName: 'Clinician-Admin'
        },
        lastUsed: new Date('2025-11-17')
      },
      {
        id: '2',
        serialNumber: '00000002',
        study: {
          id: 'study-1',
          title: 'Fake Colgate Study',
          color: '#E91E63'
        },
        clinician: {
          firstName: 'Josh',
          lastName: 'Clinician-Admin'
        },
        lastUsed: new Date('2025-11-16')
      }
    ]
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Patients"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>

          {/* Page Header */}
          <div className="mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              <PatientId patientId={patient.id || ''} size="lg" />
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Last active:{' '}
              {patient.lastActivity.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          {/* Device Usage */}
          <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Patient Devices
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Devices used by this patient
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-highlight-green">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                      Device
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                      Study
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                      Clinician
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                      Last Used
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patient.devices.map(device => (
                    <tr key={device.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {device.serialNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <ColorSquare color={device.study.color} size="sm" />
                          <span className="text-sm text-gray-900">
                            {device.study.title}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {device.clinician.firstName}{' '}
                          {device.clinician.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {device.lastUsed.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
